import { CardDetailTemplate } from '@coast/schemas';
import React, { useContext } from 'react';
import { SafeAreaView, ScrollView, StyleSheet } from 'react-native';
import LogoColorPositiveImage from '@shared/assets/images/v2/logo/color-positive.svg';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { MBCardBlocks } from '@shared/components/blocks';
import { MBCardFormProvider } from '@shared/components/blocks/contexts/form';
import { CardRenderContext } from '@shared/components/blocks/contexts/render';
import {
  CardBlockScrollContext,
  CardBlockScrollProvider,
} from '@shared/components/blocks/contexts/scroll';
import { Fields } from '@shared/components/blocks/types';
import { MBImage } from '@shared/components/image';
import { useStickyBlocks } from '@shared/scenes/cards/hooks';
import { grid } from '@shared/styles';
import { PrintContext, PrintModeContext } from '@shared/util/print/PrintContext';

interface Props {
  card: MBAPI.Card;
  template: CardDetailTemplate;
  onEdit(): void;
  onSubmit?(fields: Fields): void;
}

const styles = StyleSheet.create({
  flex: {
    flex: 1,
  },
  fieldContainer: {
    width: '100%',
    maxWidth: 456, // copied from Figma
    alignSelf: 'center',
  },
});

function Content({ card, template, onEdit }: Props) {
  const { top, scroll, bottom } = useStickyBlocks(template.cardBlocks);
  const scrollView = useContext(CardBlockScrollContext);
  const printContext = useContext(PrintContext);

  return (
    <>
      <MBCardBlocks blocks={top} card={card} sticky />
      <ScrollView
        ref={scrollView}
        style={[styles.flex, styles.fieldContainer]}
        keyboardShouldPersistTaps="always"
        keyboardDismissMode="on-drag"
      >
        <MBCardBlocks blocks={scroll} card={card} onFieldPress={onEdit} />
      </ScrollView>
      <MBView style={[styles.fieldContainer]}>
        <MBCardBlocks blocks={bottom} card={card} sticky />
      </MBView>
      {!!printContext && (
        <View style={{ width: 0, height: 0, overflow: 'hidden' }}>
          <div ref={printContext.contentRef}>
            <MBImage
              style={{ width: 164, height: 64, marginBottom: grid(2) }}
              source={LogoColorPositiveImage}
            />
            <PrintModeContext.Provider value={true}>
              <MBCardBlocks blocks={scroll} card={card} onFieldPress={onEdit} />
            </PrintModeContext.Provider>
          </div>
        </View>
      )}
    </>
  );
}

export function MBCardDetailsComponent(props: Props) {
  return (
    <CardRenderContext.Provider value="detail">
      <CardBlockScrollProvider>
        <SafeAreaView style={styles.flex}>
          <MBCardFormProvider
            card={props.card}
            blocks={props.template.cardBlocks}
            cardDefinitionId={props.card?.cardDefinitionId}
            onSubmit={props.onSubmit}
          >
            <Content {...props} />
          </MBCardFormProvider>
        </SafeAreaView>
      </CardBlockScrollProvider>
    </CardRenderContext.Provider>
  );
}
